<script setup>
import BasePage from "~/components/templates/BasePage.vue";
import Breadcrumbs from "~/components/organisms/Breadcrumbs.vue";
import Heading from "~/components/atoms/Heading.vue";
import ImageGallery from "~/components/molecules/ImageGallery.vue";
import ProductPrice from "~/components/molecules/ProductPrice.vue";
import Button from "~/components/atoms/Button.vue";
import Image from "~/components/atoms/Image.vue";
import {useCartStore} from "~/store/cartStore.js";
import CollectionsPreviewBasicBlock from "~/components/organisms/CollectionsPreviewBasicBlock.vue";
import Text from "~/components/atoms/Text.vue";
import CartIcon from "~/components/icons/CartIcon.vue";
import ProductLabels from "~/components/molecules/ProductLabels.vue";
import BasicBlock from "~/components/molecules/BasicBlock.vue";
import ExpandIcon from "~/components/icons/ExpandIcon.vue";
import CollectionsPreview from "~/components/organisms/CollectionsPreview.vue";
import Shortcode from "~/components/molecules/Shortcode.vue";
import {useUserStore} from "~/store/userStore";
import {PRODUCT_VIEW_BLOCKS, saveProductViewContext} from '~/utils/productViewContext.js';

const props = defineProps({
  data: Object
})
const {data} = props

const store = useCartStore()
const userStore = useUserStore();

const collection = data.collection
const products_data = data.products_data
const niche = data.niche_id
const related_collections = data.related_collections
const {t} = useI18n()

const onProductClick = (product, index) => {
  saveProductViewContext({
    productId: product.product_id,
    position: index + 1,
    block: PRODUCT_VIEW_BLOCKS.collectionPage,
  })
}

useHead({
  title: t('collection.collection') + ' ' + collection.name + ' - Hill Ceramic',
  meta: [
    {
      hid: 'description',
      name: 'description',
      content: t('collection.show_description', {name: collection.name})
    }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: data.breadcrumbs_structured_data
    },
  ]
})

const showMore = ref(false)
</script>

<template>
  <BasePage v-if="data" :breadcrumbs="null">
    <template v-slot:default>
      <div class="container g-32">
        <div class="row full-width top-container column-mobile" style="align-items: flex-start;">
          <div class="left-part g-24">
            <Breadcrumbs :breadcrumbs="data.breadcrumbs"/>

            <div class="g-8">
              <Heading tag="h1">
                {{ $t('collection.collection') }} <span class="focus">{{ collection.name }}</span> - Hill Ceramic
              </Heading>
              <Heading tag="h2" class="h2">
                {{ $t('collection.sub_title', {name: collection.name, niche}) }}
              </Heading>
            </div>

            <div class="description" v-html="collection.description"></div>

            <div class="attributes row g-16 wrap" v-if="collection.attributes?.length">
              <template v-for="attribute in collection.attributes">
                <div
                    v-if="!['Halvpolerad','Polerad','Blank','Relief','Matt'].includes(attribute.name)"
                    class="attribute row">
                  <div class="icon flex-center">
                    <img :src="attribute.icon" alt="">
                    <div class="hover-arrow"></div>
                    <div class="hover">
                      <div>{{ attribute.comment }}</div>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <div class="sizes row full-width g-16">
              <div class="category app no-flex"
                   v-for="size in showMore ? (collection.sizes ?? []) : (collection.sizes ?? []).slice(0,10)">{{ size.name }}
              </div>
              <div @click="showMore=true" v-if="!showMore && (collection.sizes?.length ?? 0) > 10"
                   class="category app no-flex more clickable">
                <Text tag="strong">+ {{ (collection.sizes?.length ?? 0) - 10 }}</Text>
              </div>
            </div>

            <div class="colors g-8">
              <Heading tag="h3">{{ $t('product.attributes.color') }}:</Heading>
              <nav class="panel" style="flex-wrap: wrap;">
                <nuxt-link :to="'#'+item.name" class="color" v-for="item in products_data">
                  <span>{{ item.name }}</span></nuxt-link>
              </nav>
            </div>
          </div>
          <div class="gallery-block full-width">
            <ImageGallery
                :full_images="collection.images?.length ? collection.images.map(item => ({url:item.full_image_preview})) : [{url:'/img/no-image.jpeg'}]"
                :previews="collection.images?.length ? collection.images.map(item => ({url:item.full_image_preview})) : [{url:'/img/no-image.jpeg'}]"/>
          </div>
        </div>
        <div class="products-data" v-if="products_data?.length">
          <div :id="item.name" class="products" v-for="item in products_data" style="gap:30px;">
            <header>
              <div class="row mobile-padding">
                <Heading font-size="30px" tag="h3">{{ item.name }}</Heading>
              </div>
            </header>
            <div class="images" v-if="item.images?.length">
              <div class="image image-for-preview" v-for="image in item.images" :key="image.preview">
                <Image :src="image.preview"/>
              </div>
            </div>
            <div class="previews mobile-padding">
              <article class="preview collection-preview" v-for="(product, productIndex) in item.products">
                <div class="g-24">
                  <nuxt-link :href="'/shop/'+product.url" class="full-width preview-link"
                             @click="onProductClick(product, productIndex)"
                             @auxclick="onProductClick(product, productIndex)">

                    <div class="g-24">
                      <div class="image aspect-1">
                        <Image :src="product.image"/>

                        <ProductLabels :rightDirection="true" :labels="product.labels"/>
                      </div>
                      <div class="g-8">
                        <Text class="name" tag="b" v-html="product.name"></Text>

                        <Text font-size="12px">{{ product.sku }}</Text>
                      </div>
                    </div>
                  </nuxt-link>

                  <div class="product-attributes">
                    <div class="attribute-item row" v-if="product['Size with decimals_attr']">
                      <Text>
                        <Text tag="strong">{{ $t('product.attributes.size') }}:</Text>
                        {{ product['Size with decimals_attr'] }}
                      </Text>
                    </div>
                    <div class="attribute-item row" v-if="product['Surface_attr']">
                      <Text>
                        <Text tag="strong">{{ $t('product.attributes.surface') }}:</Text>
                        {{ product['Surface_attr'] }}
                      </Text>
                    </div>
                    <div class="attribute-item row" v-if="product['Corner_attr']">
                      <Text>
                        <Text tag="strong">{{ $t('product.attributes.edge') }}:</Text>
                        {{ product['Corner_attr'] }}
                      </Text>
                    </div>
                    <div class="attribute-item row" v-if="product['Product Material_attr']">
                      <Text>
                        <Text tag="strong">{{ $t('product.attributes.material') }}:</Text>
                        {{ product['Product Material_attr'] }}
                      </Text>
                    </div>
                  </div>

                  <div class="price row g-8">
                    <ProductPrice :digits="0" :unit="product.unit" :price="product.hc_standard_price"/>
                    <template v-if="product.discount > 0 && product.hc_recommended_price > product.hc_standard_price">
                      <ProductPrice :digits="0" type="old" :unit="product.unit" :price="product.hc_recommended_price"/>
                      <Text type="red" tag="strong">-{{ product.discount }}%</Text>
                    </template>
                  </div>

                  <Button type="bordered" class="tall coarse uppercase normal-font buy"
                          @click.stop.prevent="store.addItemToCart({id:product.product_id,count:1, source:store.sources.collection_page})">
                    <CartIcon width="18px" height="18px"/> &nbsp;
                    {{ $t('cart.add_to_cart') }}
                  </Button>

                </div>
              </article>
            </div>
          </div>
        </div>

        <div class="mobile-padding">
          <section>
            <BasicBlock>
              <template v-slot:title>{{ $t("product.related_collections") }}</template>
              <nuxt-link v-if="!$device.isMobile" class="all-collections" :to="$t('collection.collections_url')">
                {{ $t('collection.see_all') }}
                <ExpandIcon/>
              </nuxt-link>
              <CollectionsPreview :collections="related_collections"/>
            </BasicBlock>

            <br>
          </section>
        </div>
      </div>
    </template>
  </BasePage>

  <Shortcode
      v-if="userStore.isAdmin"
      :shortcodes="{
        url: data?.shortcodes?.[0]?.shortcode_url ?? '',
        name: data?.shortcodes?.[0]?.shortcode_name ?? ''
    }"
  />


</template>

<style scoped lang="scss">
.name {
  min-height: 42px;
}

.all-collections {
  position: absolute;
  right: 10px;
  top: 10px;
  font-weight: 600;
  font-size: 18px;
  display: flex;
  flex-direction: row;
  align-items: center;

  @include mobile {
    position: relative;
    margin-left: auto;
    width: 100%;
    text-align: right;
    justify-content: end;
    margin-top: 20px;
    font-weight: 500;
  }
}

.more {
  background: rgba(52, 52, 52, 0.32) !important;
  color: $white;
  padding: 6px 16px;
}

.container {
  @include mobile {
    padding: 0;
  }
}

article {
  width: 100%;
  min-width: 100%;
}

.attribute-value {
  text-wrap: nowrap;
}

.h2 {
  font-weight: 400;
  font-size: 14px;
}

.buy {
  width: 215px;
}

.labels {
  position: absolute;
  bottom: 20px;
  left: -10px;

  @include smd {
    left: 0;
  }
}

.description {
  font-size: 13px;
}

.sizes {
  flex-wrap: wrap;
}

.left-part {
  max-width: 35%;
  position: sticky;
  top: 10px;

  @include smd {
    margin-right: 0;
    width: 100%;
    min-width: 100%;
    position: relative;
    padding-right: 16px;
    padding-left: 16px;
  }
}

.gallery-block {
  padding-left: 65px;
  width: 65%;

  @include smd {
    max-width: 100%;
    margin: 0;
    min-width: 100%;
    padding-left: 0;
  }
}

.top-container {
  @include smd {
    flex-direction: column-reverse;
  }
}

.previews {
  display: grid;
  grid-template-columns: repeat(4, 4fr);
  gap: 52px;
  width: 100%;
  padding-bottom: 24px;

  @include smd {
    gap: 10px;
    width: 100%;
    margin: 0;
    grid-template-columns: repeat(2, 2fr);
    row-gap: 30px;
  }

  .preview {
    @include smd {
      margin: 0;
    }

    .name {
      height: 62px;

      @include smd {
        font-size: 12px;
        height: unset;
        margin-bottom: 10px;
      }
    }

    .price {
      @include mobile {
        font-size: 12px;

        :deep(.price),
        :deep(.price.old) {
          font-size: 12px;
        }

        :deep(.price sup) {
          font-size: 9px;
        }
      }
    }

    .product-attributes {
      @include smd {
        font-size: 11px;
      }

      .attribute-item {
        gap: 4px;

        .attribute-name {
          font-weight: 600;
        }
      }
    }
  }
}

.products {
  padding-top: 16px;
  border-top: 1px solid $border-grey;

  @include smd {
    padding-top: 20px;
    margin-top: 20px;
  }
}

.images {
  display: grid;
  grid-template-columns: repeat(3, 3fr);
  gap: 6px;

  @include smd {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto;
    gap: 10px;
  }

  .image {
    aspect-ratio: 1 / 1;
    width: 100%;

    &:nth-child(3n) {
      @include smd {
        display: none;
      }
    }
  }
}

.category {
  background: #efefef;
  padding: 5px 10px;
}

.color {
  margin-right: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.color .square {
  min-width: 31px;
  min-height: 30px;
  border-radius: 3px;
}

.color span {
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  font-style: normal;
  letter-spacing: normal;
  line-height: normal;
  color: black;
  text-decoration: underline;
  margin-top: 5px;
}

.panel {
  display: flex;
}

.colors {
  &:deep(h3) {
    color: #707070;
    font-size: 14px;
  }
}

.focus {
  background: $space-black;
  color: $white;
  padding: 6px 16px;
}

.attributes .attribute .icon {
  width: 48px;
  height: 48px;
  max-width: 48px;
  max-height: 48px;
  border-radius: 100%;
  background-color: #f5f5f5;

  @include smd {
    width: 42px;
    height: 42px;
  }

  .hover-arrow {
    position: absolute;
    border-bottom: 14px solid $black !important;
    border-left: 11px solid transparent;
    border-right: 11px solid transparent;
    border-radius: 4px;
    color: $white;
    text-align: center;
    left: 50%;
    transform: translateX(-50%);
    display: none;
    z-index: 9;
    top: 43px;
    max-width: 10px;
    width: 0;
    height: 0;
  }

  .hover {
    position: absolute;
    background: $black;
    border-radius: 4px;
    color: $white;
    text-align: center;
    line-height: 16px;
    font-size: 11px;
    font-weight: 300;
    padding: 5px 10px;
    left: 50%;
    transform: translateX(-50%);
    display: none;
    z-index: 9;
    top: 55px;
    max-width: 250px;
    width: max-content;
  }

  &:hover {
    background: $yellow;

    .hover-arrow {
      display: block;
    }

    .hover {
      display: block;
    }
  }
}

.attributes .attribute .icon img {
  height: 32px;
  display: flex;
  align-self: center;
  justify-self: center;
}

.attribute .attr-info span {
  margin-left: 25px;
  font-size: 18px;
  font-weight: 400;
  font-style: normal;
  letter-spacing: normal;
  line-height: 26px;
  text-align: left;

  @include smd {
    font-size: 13px;
    font-weight: 600;
  }

  &:last-child {
    font-size: 15px;

    @include smd {
      font-size: 9px;
      font-weight: 400;
      line-height: 15px;
    }
  }

  .attributes .attribute .attr-info span:last-child {
    font-size: 15px;
  }
}
</style>
